/**
 * The outbox is the app's offline message bus. Every change made on this device is written to the local
 * database and appended to the outbox in the same IndexedDB transaction, so the screen and the queue can never
 * disagree. The sync engine later delivers outbox messages to the server strictly in the order they were
 * created (the auto-incrementing `seq`), one owner at a time.
 */
import { db, entityKey, setMeta, tableFor, type DocumentOp, type FileOp, type OutboxOp, type RecordOp, type Stored } from '../data/db';
import { normalize, serverValue } from '../data/normalize';
import type { ComplianceDocument, EntityMap, Namespace } from '../data/types';
import { nowIso, uid } from '../lib/format';

let ownerId = '';
const enqueueListeners = new Set<() => void>();

/** The signed-in account. New messages are stamped with it and only its messages are sent. */
export function setOutboxOwner(id: string) { ownerId = id; }
export const outboxOwner = () => ownerId;

export function onEnqueue(listener: () => void) {
  enqueueListeners.add(listener);
  return () => { enqueueListeners.delete(listener); };
}
const notify = () => enqueueListeners.forEach((listener) => listener());

function base(entity: string, label: string) {
  if (!ownerId) throw new Error('Sign in before making changes.');
  return { opId: uid('op_'), ownerId, entityKey: entity, label, status: 'pending' as const, attempts: 0, createdAt: nowIso() };
}

/** Saves an entity locally and queues it for the server. */
export async function putEntity<N extends Namespace>(namespace: N, value: EntityMap[N], label: string): Promise<void> {
  const table = tableFor(namespace);
  const clean = serverValue(value as EntityMap[N] & object);
  await db.transaction('rw', table, db.outbox, async () => {
    const existing = await table.get(value.id);
    // The revision this device last saw. Several offline edits all carry it; the server re-bases them in order.
    const baseRevision = existing?._rev ?? null;
    await table.put({ ...clean, _rev: existing?._rev, _updatedAt: nowIso() } as Stored<EntityMap[N]>);
    await db.outbox.add({ ...base(entityKey(namespace, value.id), label), kind: 'record', type: 'put', namespace, key: value.id, value: clean, baseRevision } satisfies RecordOp);
  });
  notify();
}

export async function deleteEntity(namespace: Namespace, id: string, label: string): Promise<void> {
  const table = tableFor(namespace);
  await db.transaction('rw', table, db.outbox, async () => {
    const existing = await table.get(id);
    await table.delete(id);
    await db.outbox.add({ ...base(entityKey(namespace, id), label), kind: 'record', type: 'delete', namespace, key: id, baseRevision: existing?._rev ?? null } satisfies RecordOp);
  });
  notify();
}

/**
 * Stores a file on the device and queues its upload. Queue the file before the record that references it:
 * because delivery is first-in, first-out, the server always has the file by the time the record arrives.
 */
export async function queueFile(blob: Blob, meta: FileOp['meta'], label: string, fileId = uid('file_')): Promise<string> {
  await db.transaction('rw', db.blobs, db.outbox, async () => {
    await db.blobs.put({ id: fileId, blob, name: meta.name, type: meta.type, size: blob.size, createdAt: nowIso(), uploaded: false });
    await db.outbox.add({ ...base(`file/${fileId}`, label), kind: 'file', fileId, meta } satisfies FileOp);
  });
  notify();
  return fileId;
}

export async function queueDocument(action: DocumentOp['action'], document: ComplianceDocument, label: string, blob?: Blob): Promise<void> {
  const blobId = blob ? `doc-upload:${document.id}:${Date.now()}` : undefined;
  const meta = { name: document.name, documentName: document.documentName, tag: document.tag, expiry: document.expiry, issuedDate: document.issuedDate, reviewDateAutoFilled: document.reviewDateAutoFilled, type: document.type };
  await db.transaction('rw', [db.documents, db.blobs, db.outbox], async () => {
    if (blob && blobId) await db.blobs.put({ id: blobId, blob, name: document.name, type: document.type, size: blob.size, createdAt: nowIso(), uploaded: false });
    if (action === 'delete') await db.documents.delete(document.id);
    else await db.documents.put({ ...document, pending: true });
    await db.outbox.add({ ...base(`doc/${document.id}`, label), kind: 'document', action, documentId: document.id, meta, blobId } satisfies DocumentOp);
  });
  notify();
}

export function needsReview(op: OutboxOp) { return op.status !== 'pending'; }

/** Messages for one account in delivery order. */
export async function ownerQueue(owner = ownerId): Promise<OutboxOp[]> {
  return db.outbox.where('ownerId').equals(owner).sortBy('seq');
}

async function laterOpsFor(op: OutboxOp) {
  return (await db.outbox.where('entityKey').equals(op.entityKey).toArray()).filter((item) => (item.seq ?? 0) > (op.seq ?? 0));
}

/**
 * Resolves a conflict. "server" drops this device's queued changes to that record and adopts the server copy;
 * "mine" re-bases the queued changes on the server's revision so they overwrite it in order.
 */
export async function resolveConflict(seq: number, choice: 'mine' | 'server'): Promise<void> {
  const op = await db.outbox.get(seq);
  if (!op || op.kind !== 'record') return;
  const serverRevision = op.server?.revision ?? null;
  const table = tableFor(op.namespace);
  await db.transaction('rw', table, db.outbox, async () => {
    const later = await laterOpsFor(op);
    if (choice === 'server') {
      await db.outbox.bulkDelete([op.seq!, ...later.map((item) => item.seq!)]);
      if (!op.server || op.server.deleted || op.server.value === null) await table.delete(op.key);
      else await table.put({ ...normalize(op.namespace, op.server.value, op.key), _rev: op.server.revision, _updatedAt: op.server.updatedAt } as Stored<EntityMap[typeof op.namespace]>);
      return;
    }
    await db.outbox.update(seq, { status: 'pending', baseRevision: serverRevision, server: null, lastError: undefined } as Partial<RecordOp>);
    for (const item of later) if (item.kind === 'record' && item.baseRevision === op.baseRevision) await db.outbox.update(item.seq!, { baseRevision: serverRevision } as Partial<RecordOp>);
    const row = await table.get(op.key);
    if (row && serverRevision !== null) await table.update(op.key, { _rev: serverRevision } as never);
  });
  notify();
}

/** Puts a rejected message back in the queue, e.g. after an administrator changed permissions. */
export async function retryOp(seq: number): Promise<void> {
  await db.outbox.update(seq, { status: 'pending', lastError: undefined });
  notify();
}

/** Abandons a message that can never be delivered. The local copy is replaced by the server's on the next pull. */
export async function discardOp(seq: number): Promise<void> {
  const op = await db.outbox.get(seq);
  if (!op) return;
  await db.outbox.delete(seq);
  if (op.kind === 'record') {
    const others = await db.outbox.where('entityKey').equals(op.entityKey).count();
    if (!others) await tableFor(op.namespace).delete(op.key);
    // Re-download everything on the next sync so the server copy of this record comes back.
    await setMeta('sync-cursor', 0);
  }
  notify();
}
